import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import en from './en.json';
import vi from './vi.json';

/**
 * A tiny, dependency-free i18n layer. It mirrors the slice of the react-i18next
 * API the app uses — `useTranslation()` → `{ t, i18n: { language,
 * changeLanguage } }` — so pages can migrate to a real library later without
 * changing call sites. English is the fallback, so any key missing from a
 * translation (or not yet added to a page) still renders its English string.
 */

export const SUPPORTED_LANGUAGES = [
  { code: 'en', labelKey: 'common.english' },
  { code: 'vi', labelKey: 'common.vietnamese' },
] as const;

export type AppLanguage = (typeof SUPPORTED_LANGUAGES)[number]['code'];

const STORAGE_KEY = 'aquatics_hub_2025_lang';

const resources: Record<AppLanguage, Record<string, unknown>> = { en, vi };

const getInitialLanguage = (): AppLanguage => {
  const stored = localStorage.getItem(STORAGE_KEY);
  return stored === 'en' || stored === 'vi' ? stored : 'en';
};

/** Resolve a dotted key ("masterData.title") to a string, if present. */
const lookup = (
  dict: Record<string, unknown>,
  key: string,
): string | undefined => {
  const value = key.split('.').reduce<unknown>((accumulator, part) => {
    if (accumulator && typeof accumulator === 'object') {
      return (accumulator as Record<string, unknown>)[part];
    }
    return undefined;
  }, dict);
  return typeof value === 'string' ? value : undefined;
};

/** Replace `{{name}}` placeholders with values from `vars`. */
const interpolate = (
  template: string,
  vars?: Record<string, unknown>,
): string => {
  if (!vars) return template;
  return template.replace(/\{\{(\w+)\}\}/g, (_match, name: string) =>
    name in vars ? String(vars[name]) : `{{${name}}}`,
  );
};

type TFunction = (key: string, vars?: Record<string, unknown>) => string;

type I18nContextValue = {
  language: AppLanguage;
  changeLanguage: (language: string) => void;
  t: TFunction;
};

const I18nContext = createContext<I18nContextValue | null>(null);

export const I18nProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguage] = useState<AppLanguage>(getInitialLanguage);

  const changeLanguage = useCallback((next: string) => {
    const normalized: AppLanguage = next === 'vi' ? 'vi' : 'en';
    localStorage.setItem(STORAGE_KEY, normalized);
    setLanguage(normalized);
  }, []);

  const t = useCallback<TFunction>(
    (key, vars) => {
      const template =
        lookup(resources[language], key) ?? lookup(en, key) ?? key;
      return interpolate(template, vars);
    },
    [language],
  );

  const value = useMemo<I18nContextValue>(
    () => ({ language, changeLanguage, t }),
    [language, changeLanguage, t],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

/** Drop-in shape-compatible replacement for react-i18next's useTranslation. */
export const useTranslation = () => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useTranslation must be used within an I18nProvider.');
  }
  return {
    t: context.t,
    i18n: {
      language: context.language,
      changeLanguage: context.changeLanguage,
    },
  };
};
